import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import logo from '../../../../assets/images/rio-claro-logo-colorida-2.svg';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { useToast } from '../../ui/toast/ToastContext';

const ClientSidebar = ({
  items = [],
  currentPath,
  currentClient,
  open = false, // no mobile: drawer aberto?
  onClose = () => {}, // fechar drawer (mobile)
  isMobile = false,
}) => {
  const [signingOut, setSigningOut] = useState(false);
  const { addToast } = useToast();

  // trava o scroll do body quando o drawer está aberto no mobile
  useEffect(() => {
    if (!isMobile) return;
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open, isMobile]);

  const handleSignOut = async (e) => {
    e.preventDefault();
    if (signingOut) return;
    setSigningOut(true);
    try {
      const res = await fetchWithCsrf('/clients/sign_out', { method: 'DELETE' });
      if (res.ok) {
        window.location.assign('/');
      } else {
        window.location.assign('/');
      }
    } catch (err) {
      addToast?.({ type: 'error', message: 'Falha de rede ao sair. Verifique sua conexão.' });
      setSigningOut(false);
    }
  };

  const AsideContent = (
    <aside className="w-72 bg-blueDark text-white h-full flex flex-col">
      {/* Logo */}
      <div className="flex items-center h-20 px-6 shrink-0 justify-center bg-white">
        <img src={logo} alt="Rio Claro Investimentos" className="h-11" />
      </div>

      {/* Perfil do cliente */}
      <div className="flex flex-col items-center gap-3 px-6 py-12 bg-blueDarker">
        {currentClient?.avatarUrl ? (
          <img src={currentClient.avatarUrl} alt="Avatar" className="w-28 h-28 rounded-full object-cover" />
        ) : (
          <div className="w-28 h-28 rounded-full bg-white flex items-center justify-center">
            <Icons.User2 className="w-10 h-10 text-blueMedium" />
          </div>
        )}
        <div className="flex-1 min-w-0 justify-center text-center">
          <p className="text-xl opacity-90">
            Olá, <span className="font-semibold truncate">{currentClient?.firstName}</span>
          </p>
          <a href="/app/client/account" className="text-sm underline hover:opacity-90 text-grayExtraMedium">
            Editar perfil
          </a>
        </div>
      </div>

      {/* Itens do menu */}
      <nav className="flex-1 overflow-auto">
        <ul className="flex flex-col">
          {items.map((item, idx) => {
            if (!item.visible) return null;

            const Icon = Icons[item.icon] || Icons.Circle;
            const active = currentPath === item.href;
            return (
              <li key={idx}>
                <a
                  href={item.href}
                  className={`flex items-center gap-3 px-6 py-5 transition-colors
                    ${active ? 'bg-blueMedium border-r-4 border-tealLight font-semibold' : 'hover:bg-white/50'}`}
                  onClick={() => isMobile && onClose()}
                >
                  <Icon size={20} strokeWidth={1.5} className={active ? 'text-tealSoft' : 'text-white'} />
                  <span className="text-base">{item.label}</span>
                </a>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Sair */}
      <div className="px-3 py-4">
        <button
          type="button"
          onClick={handleSignOut}
          disabled={signingOut}
          className={`flex gap-3 items-center w-full px-4 py-5 text-base font-normal transition-colors duration-200
                      ${signingOut ? 'opacity-70 cursor-not-allowed' : 'text-white hover:text-red-400'}`}
        >
          <Icons.LogOut size={20} strokeWidth={1.5} />
          <span>{signingOut ? 'Saindo...' : 'Sair'}</span>
        </button>
      </div>
    </aside>
  );

  if (!isMobile) {
    // Desktop: fixa
    return <div className="h-full">{AsideContent}</div>;
  }

  // Mobile: drawer + overlay
  return (
    <>
      {/* overlay */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black bg-opacity-40 backdrop-filter backdrop-blur-sm" onClick={onClose} />
      )}

      {/* drawer */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-200
          ${open ? 'translate-x-0' : '-translate-x-full'}`}
        aria-hidden={!open}
      >
        <div className="h-full shadow-xl">{AsideContent}</div>
      </div>
    </>
  );
};

export default ClientSidebar;
